import type { BlockStorageQuota, BlockVolume } from '@xver/apis/v1/block-storage/model';
import { allowedActions, resizeBlockedReason } from '@xver/utils/blockStorage';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import RowActionButton from './RowActionButton';

export type VolumeAction = 'attach' | 'detach' | 'resize' | 'delete';

interface VolumeRowActionsProps {
    volume: BlockVolume;
    /** Actions to offer, in order; `attach` and `detach` each mean the one slot that follows the attachment. */
    actions: readonly VolumeAction[];
    /** The owner's limits and usage, from `resolveQuota`. Pass it with `resize` so a volume that can't grow gets a blocked button. */
    quota?: BlockStorageQuota;
    onAction: (action: VolumeAction, volume: BlockVolume) => void;
}

/**
 * The same buttons render in every status, so a status change while a button has focus never
 * removes it. Blocked ones are described by the reasons shown as text under the buttons.
 */
export default function VolumeRowActions({
    volume,
    actions,
    quota,
    onAction,
}: VolumeRowActionsProps) {
    const { t } = useTranslation('blockStorage');
    const reasonIdPrefix = useId();
    const allowed = allowedActions(volume);
    const busyReason =
        volume.status === 'error'
            ? t('blocked.error')
            : t('blocked.busy', { status: t(`status.${volume.status}`).toLowerCase() });

    const growBlocked = quota ? resizeBlockedReason(quota, volume.sizeGiB) : null;
    const growReason =
        quota && growBlocked
            ? t(`blocked.resize.${growBlocked}`, { max: quota.maxVolumeGiB, limit: quota.limitGiB })
            : undefined;

    const reasonFor = (action: VolumeAction): string | undefined => {
        if (allowed[action]) return action === 'resize' ? growReason : undefined;
        if (action === 'delete' && volume.status === 'attached') return t('blocked.deleteAttached');
        return busyReason;
    };

    const slots = actions.map(
        (action): VolumeAction =>
            action === 'attach' || action === 'detach'
                ? volume.attachment
                    ? 'detach'
                    : 'attach'
                : action
    );

    const reasons = [
        ...new Set(slots.map(reasonFor).filter((reason): reason is string => !!reason)),
    ];
    const reasonId = (reason: string | undefined) =>
        reason ? `${reasonIdPrefix}-${reasons.indexOf(reason)}` : undefined;

    return (
        <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap justify-end gap-1.5">
                {slots.map((action, index) => (
                    <RowActionButton
                        key={index}
                        label={t(`actions.${action}`)}
                        name={volume.name}
                        blockedReasonId={reasonId(reasonFor(action))}
                        danger={action === 'delete'}
                        onClick={() => onAction(action, volume)}
                    />
                ))}
            </div>
            {reasons.map((reason) => (
                <p
                    key={reason}
                    id={reasonId(reason)}
                    className="max-w-72 text-right text-xs text-fg/68"
                >
                    {reason}
                </p>
            ))}
        </div>
    );
}
